import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Users, FileText, Bell, LogOut, Plus, Trash2, KeyRound, Upload, Download, Building2, Plane, Package, Award, Layers, FileEdit, ChevronDown, ChevronRight, Shield, BookOpen } from 'lucide-react';
import ShortlistManager from '../components/admin/ShortlistManager';
import MandateManager from '../components/admin/MandateManager';
import MandateTemplateEditor from '../components/admin/MandateTemplateEditor';
import DisclosureRegistry from '../components/admin/DisclosureRegistry';
import ContractManager from '../components/admin/ContractManager';
import ContractTemplateEditor from '../components/admin/ContractTemplateEditor';
import ManufacturerAgreementTemplateEditor from '../components/admin/ManufacturerAgreementTemplateEditor';
import api from '../api';
import { useAuth } from '../auth/AuthContext';
import { useToast } from '../hooks/use-toast';
import Header from '../components/Header';
import Footer from '../components/Footer';
import MissionsManager from '../components/admin/MissionsManager';
import RescuedManager from '../components/admin/RescuedManager';
import CompaniesManager from '../components/admin/CompaniesManager';
import { COMMODITIES, commodityLabel } from '../constants/commodities';

const CATEGORIES = [
  { id: 'loi', label: 'Letter of Interest (LOI)' },
  { id: 'icpo', label: 'ICPO' },
  { id: 'banking', label: 'Coordonnées bancaires' },
  { id: 'passport', label: 'Passeports' },
  { id: 'license', label: "Licences d'exploitation" },
  { id: 'other', label: 'Autres' },
];

function formatSize(b) {
  if (b < 1024) return `${b} o`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} Ko`;
  return `${(b / 1024 / 1024).toFixed(1)} Mo`;
}

export default function AdminPage() {
  const { user, logout } = useAuth();
  const { toast } = useToast();
  const nav = useNavigate();
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [docs, setDocs] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [rfqs, setRfqs] = useState([]);
  const [openRfq, setOpenRfq] = useState(null);

  const [newUser, setNewUser] = useState({ username: '', password: '', displayName: '', company: '', email: '', commodities: [] });
  const [newDoc, setNewDoc] = useState({ title: '', category: 'loi', owner: 'yuville', commodity: 'autre', description: '', file: null });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [u, d, up, n, r] = await Promise.all([
        api.get('/admin/users'),
        api.get('/admin/documents'),
        api.get('/extranet/uploads'),
        api.get('/admin/notifications'),
        api.get('/admin/rfq'),
      ]);
      setUsers(u.data || []); setDocs(d.data || []); setUploads(up.data || []); setNotifs(n.data || []); setRfqs(r.data || []);
    } catch (err) {
      if (err?.response?.status === 401) { logout(); nav('/extranet/login'); }
    }
  };

  useEffect(() => { load(); }, []);

  const createUser = async (e) => {
    e.preventDefault();
    if (!newUser.username || !newUser.password) return;
    setBusy(true);
    try {
      await api.post('/admin/users', newUser);
      toast({ title: 'Utilisateur créé' });
      setNewUser({ username: '', password: '', displayName: '', company: '', email: '', commodities: [] });
      load();
    } catch (err) {
      console.error('createUser error', err?.response || err);
      const detail = err?.response?.data?.detail;
      const detailStr = Array.isArray(detail) ? detail.map((d) => `${d.loc?.slice(-1)[0]}: ${d.msg}`).join(' • ') : (detail || err?.message || 'Création impossible.');
      toast({ title: `Erreur ${err?.response?.status || ''}`.trim(), description: detailStr, duration: 15000 });
    } finally { setBusy(false); }
  };

  const deleteUser = async (id) => {
    if (!window.confirm('Supprimer cet utilisateur ?')) return;
    await api.delete(`/admin/users/${id}`);
    load();
  };

  const resetPwd = async (id) => {
    const pwd = window.prompt('Nouveau mot de passe :');
    if (!pwd) return;
    await api.post(`/admin/users/${id}/reset-password`, { password: pwd });
    toast({ title: 'Mot de passe modifié' });
  };

  const uploadDoc = async (e) => {
    e.preventDefault();
    if (!newDoc.file || !newDoc.title) {
      toast({ title: 'Champs requis', description: 'Titre et fichier requis.' }); return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', newDoc.file);
      fd.append('title', newDoc.title);
      fd.append('category', newDoc.category);
      fd.append('owner', newDoc.owner);
      fd.append('commodity', newDoc.commodity);
      fd.append('description', newDoc.description);
      await api.post('/admin/documents', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast({ title: 'Document téléversé' });
      setNewDoc({ title: '', category: 'loi', owner: 'yuville', commodity: 'autre', description: '', file: null });
      load();
    } catch (err) {
      console.error('uploadDoc error', err?.response || err);
      const detail = err?.response?.data?.detail;
      const detailStr = Array.isArray(detail) ? detail.map((d) => `${d.loc?.slice(-1)[0]}: ${d.msg}`).join(' • ') : (detail || err?.message || 'Upload impossible.');
      toast({ title: `Erreur ${err?.response?.status || ''}`.trim(), description: detailStr, duration: 15000 });
    } finally { setBusy(false); }
  };

  const deleteDoc = async (id) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    await api.delete(`/admin/documents/${id}`);
    load();
  };

  const downloadDoc = async (d) => {
    const res = await api.get(`/extranet/documents/${d.id}/download`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a'); a.href = url; a.download = d.filename;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const markRead = async (id) => {
    await api.post(`/admin/notifications/${id}/read`);
    load();
  };

  const unreadCount = notifs.filter((n) => !n.read).length;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header onCTA={() => nav('/')} />

      <main className="flex-1 pt-28 pb-16">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
            <div>
              <div className="text-xs text-brand-yellow font-bold tracking-widest uppercase mb-1">Panneau Admin</div>
              <h1 className="text-3xl md:text-4xl font-bold text-brand-navy">Yuville • Extranet Management</h1>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/extranet" className="px-4 py-2 rounded border border-gray-300 text-brand-navy text-sm hover:border-brand-yellow transition">Vue Producteur</Link>
              <button onClick={() => { logout(); nav('/'); }} className="inline-flex items-center gap-2 px-4 py-2 rounded border border-gray-300 text-brand-navy text-sm hover:border-brand-yellow transition">
                <LogOut className="w-4 h-4" /> Déconnexion
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap gap-2 mb-6">
            {[
              { id: 'users', label: 'Utilisateurs', icon: Users, count: users.length },
              { id: 'docs', label: 'Documents Yuville/Qianyan', icon: FileText, count: docs.length },
              { id: 'uploads', label: 'Dépôts producteurs', icon: Upload, count: uploads.length },
              { id: 'missions', label: 'Missions', icon: Plane },
              { id: 'rescued', label: 'Produits rescapés', icon: Package },
              { id: 'companies', label: 'Entreprises du mois', icon: Award },
              { id: 'rfq', label: 'Cahiers des charges', icon: FileEdit, count: rfqs.length },
              { id: 'mandate', label: 'Mandat & Registre', icon: Shield },
              { id: 'notifs', label: 'Notifications', icon: Bell, count: unreadCount },
            ].map((t) => {
              const Ic = t.icon;
              return (
                <button key={t.id} onClick={() => setTab(t.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition ${tab === t.id ? 'bg-brand-navy text-white border-brand-navy' : 'bg-white text-brand-navy border-gray-200 hover:border-brand-yellow'}`}>
                  <Ic className="w-4 h-4" /> {t.label} {t.count > 0 && <span className={`px-2 py-0.5 rounded-full text-xs ${tab === t.id ? 'bg-brand-yellow text-brand-navy' : 'bg-gray-100'}`}>{t.count}</span>}
                </button>
              );
            })}
          </div>

          {tab === 'users' && (
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl border border-gray-100 p-6">
                <h2 className="font-bold text-brand-navy mb-4">Créer un nouvel utilisateur producteur</h2>
                <form onSubmit={createUser} className="space-y-3">
                  <input value={newUser.username} onChange={(e) => setNewUser({ ...newUser, username: e.target.value })} placeholder="Nom d'usager *" className="w-full px-4 py-2.5 border border-gray-200 rounded focus:outline-none focus:border-brand-yellow" />
                  <input value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} placeholder="Mot de passe *" className="w-full px-4 py-2.5 border border-gray-200 rounded focus:outline-none focus:border-brand-yellow" />
                  <input value={newUser.displayName} onChange={(e) => setNewUser({ ...newUser, displayName: e.target.value })} placeholder="Nom complet" className="w-full px-4 py-2.5 border border-gray-200 rounded focus:outline-none focus:border-brand-yellow" />
                  <input value={newUser.company} onChange={(e) => setNewUser({ ...newUser, company: e.target.value })} placeholder="Entreprise" className="w-full px-4 py-2.5 border border-gray-200 rounded focus:outline-none focus:border-brand-yellow" />
                  <input value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} placeholder="Email" className="w-full px-4 py-2.5 border border-gray-200 rounded focus:outline-none focus:border-brand-yellow" />
                  <div>
                    <div className="text-xs font-semibold text-brand-navy uppercase tracking-wider mb-2">Matières autorisées</div>
                    <div className="flex flex-wrap gap-2">
                      {COMMODITIES.map((c) => (
                        <label key={c.id} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs cursor-pointer transition ${newUser.commodities.includes(c.id) ? 'bg-brand-yellow text-brand-navy border-brand-yellow font-semibold' : 'bg-white text-brand-navy border-gray-200 hover:border-brand-yellow'}`}>
                          <input type="checkbox" className="hidden" checked={newUser.commodities.includes(c.id)}
                            onChange={(e) => {
                              const arr = e.target.checked
                                ? [...newUser.commodities, c.id]
                                : newUser.commodities.filter((x) => x !== c.id);
                              setNewUser({ ...newUser, commodities: arr });
                            }} />
                          {c.label.fr}
                        </label>
                      ))}
                    </div>
                  </div>
                  <button disabled={busy} className="btn-yellow w-full justify-center"><Plus className="w-4 h-4" /> Créer</button>
                </form>
              </div>

              <div className="bg-white rounded-xl border border-gray-100 p-6">
                <h2 className="font-bold text-brand-navy mb-4">Utilisateurs actifs ({users.length})</h2>
                {users.length === 0 ? <p className="text-sm text-gray-400 italic">Aucun utilisateur.</p> : (
                  <ul className="space-y-2 max-h-[500px] overflow-y-auto">
                    {users.map((u) => (
                      <li key={u.id} className="p-3 rounded border border-gray-100">
                        <div className="flex items-center justify-between mb-2">
                          <div className="min-w-0">
                            <div className="font-semibold text-brand-navy text-sm">{u.username} {u.company && <span className="text-gray-500">• {u.company}</span>}</div>
                            <div className="text-xs text-gray-500 truncate">{u.displayName} {u.email && `• ${u.email}`}</div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <button onClick={() => resetPwd(u.id)} className="p-2 rounded hover:bg-gray-100" title="Réinitialiser mot de passe"><KeyRound className="w-4 h-4 text-brand-navy" /></button>
                            <button onClick={() => deleteUser(u.id)} className="p-2 rounded hover:bg-red-50" title="Supprimer"><Trash2 className="w-4 h-4 text-red-600" /></button>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {COMMODITIES.map((c) => {
                            const active = (u.commodities || []).includes(c.id);
                            return (
                              <button key={c.id} onClick={async () => {
                                const arr = active ? (u.commodities || []).filter((x) => x !== c.id) : [...(u.commodities || []), c.id];
                                await api.put(`/admin/users/${u.id}/commodities`, { commodities: arr });
                                load();
                              }} className={`text-[10px] px-2 py-0.5 rounded-full border transition ${active ? 'bg-brand-yellow text-brand-navy border-brand-yellow font-semibold' : 'bg-white text-gray-500 border-gray-200 hover:border-brand-yellow'}`}>
                                {c.label.fr}
                              </button>
                            );
                          })}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          {tab === 'docs' && (
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl border border-gray-100 p-6">
                <h2 className="font-bold text-brand-navy mb-4">Téléverser un document</h2>
                <form onSubmit={uploadDoc} className="space-y-3">
                  <input value={newDoc.title} onChange={(e) => setNewDoc({ ...newDoc, title: e.target.value })} placeholder="Titre *" className="w-full px-4 py-2.5 border border-gray-200 rounded focus:outline-none focus:border-brand-yellow" />
                  <div className="grid grid-cols-2 gap-3">
                    <select value={newDoc.category} onChange={(e) => setNewDoc({ ...newDoc, category: e.target.value })} className="px-4 py-2.5 border border-gray-200 rounded bg-white">
                      {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                    </select>
                    <select value={newDoc.owner} onChange={(e) => setNewDoc({ ...newDoc, owner: e.target.value })} className="px-4 py-2.5 border border-gray-200 rounded bg-white">
                      <option value="yuville">Yuville</option>
                      <option value="qianyan">Qianyan</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-brand-navy uppercase mb-1">Matière première</label>
                    <select value={newDoc.commodity} onChange={(e) => setNewDoc({ ...newDoc, commodity: e.target.value })} className="w-full px-4 py-2.5 border border-gray-200 rounded bg-white">
                      {COMMODITIES.map((c) => <option key={c.id} value={c.id}>{c.label.fr}</option>)}
                    </select>
                  </div>
                  <textarea rows={2} value={newDoc.description} onChange={(e) => setNewDoc({ ...newDoc, description: e.target.value })} placeholder="Description" className="w-full px-4 py-2.5 border border-gray-200 rounded focus:outline-none focus:border-brand-yellow resize-none" />
                  <input type="file" onChange={(e) => setNewDoc({ ...newDoc, file: e.target.files?.[0] || null })} className="w-full px-4 py-2 border border-gray-200 rounded file:mr-3 file:border-0 file:bg-brand-navy file:text-white file:px-3 file:py-1.5 file:rounded file:text-xs" />
                  <button disabled={busy} className="btn-yellow w-full justify-center"><Upload className="w-4 h-4" /> Téléverser</button>
                </form>
              </div>

              <div className="bg-white rounded-xl border border-gray-100 p-6">
                <h2 className="font-bold text-brand-navy mb-4">Documents actuels ({docs.length})</h2>
                {docs.length === 0 ? <p className="text-sm text-gray-400 italic">Aucun document.</p> : (
                  <ul className="space-y-2 max-h-[500px] overflow-y-auto">
                    {docs.map((d) => (
                      <li key={d.id} className="p-3 rounded border border-gray-100 flex items-center justify-between">
                        <div className="min-w-0">
                          <div className="font-semibold text-brand-navy text-sm truncate flex items-center gap-2 flex-wrap">
                            {d.title}
                            <span className="text-[10px] px-2 py-0.5 bg-gray-100 rounded uppercase font-bold">{d.owner}</span>
                            <span className="text-[10px] px-2 py-0.5 bg-brand-yellow/20 text-brand-navy rounded uppercase font-bold">{commodityLabel(d.commodity, 'fr')}</span>
                          </div>
                          <div className="text-xs text-gray-500 truncate">{d.filename} • {formatSize(d.size)} • {d.category}</div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button onClick={() => downloadDoc(d)} className="p-2 rounded hover:bg-gray-100"><Download className="w-4 h-4 text-brand-navy" /></button>
                          <button onClick={() => deleteDoc(d.id)} className="p-2 rounded hover:bg-red-50"><Trash2 className="w-4 h-4 text-red-600" /></button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          {tab === 'uploads' && (
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h2 className="font-bold text-brand-navy mb-4">Dépôts des producteurs ({uploads.length})</h2>
              {uploads.length === 0 ? <p className="text-sm text-gray-400 italic">Aucun dépôt reçu.</p> : (
                <ul className="space-y-2">
                  {uploads.map((u) => (
                    <li key={u.id} className="p-3 rounded border border-gray-100 flex items-center justify-between">
                      <div className="min-w-0">
                        <div className="font-semibold text-brand-navy text-sm truncate">{u.title}</div>
                        <div className="text-xs text-gray-500 truncate">Par {u.uploadedBy} • {u.filename} • {formatSize(u.size)} • {new Date(u.createdAt).toLocaleString()}</div>
                      </div>
                      <button onClick={() => downloadDoc(u)} className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded bg-brand-yellow text-brand-navy text-xs font-semibold"><Download className="w-3.5 h-3.5" /> Télécharger</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {tab === 'missions' && <MissionsManager />}
          {tab === 'rescued' && <RescuedManager />}
          {tab === 'companies' && <CompaniesManager />}

          {tab === 'rfq' && (
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h2 className="font-bold text-brand-navy mb-4">Cahiers des charges reçus ({rfqs.length})</h2>
              {rfqs.length === 0 ? <p className="text-sm text-gray-400 italic">Aucune demande reçue.</p> : (
                <ul className="space-y-2">
                  {rfqs.map((r) => {
                    const isOpen = openRfq === r.id;
                    return (
                      <li key={r.id} className={`rounded border ${isOpen ? 'border-brand-yellow bg-brand-yellow/5' : 'border-gray-100'}`}>
                        <button onClick={() => setOpenRfq(isOpen ? null : r.id)} className="w-full flex items-center justify-between p-4 text-left">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${r.status === 'new' ? 'bg-brand-yellow text-brand-navy' : 'bg-gray-200 text-gray-700'}`}>{r.status || 'new'}</span>
                              <div className="font-semibold text-brand-navy text-sm truncate">{r.contact?.name} · {r.contact?.company || '—'}</div>
                            </div>
                            <div className="text-xs text-gray-500 mt-1 truncate">
                              {r.project?.type}{r.project?.typeOther ? ` (${r.project.typeOther})` : ''} • {r.need?.product || 'sans titre'} • {new Date(r.createdAt).toLocaleString()} • {r.filesCount} fichier(s)
                            </div>
                          </div>
                          {isOpen ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                        </button>
                        {isOpen && (
                          <div className="border-t border-gray-200 p-4 space-y-4 text-sm">
                            <RfqField label="Contact">
                              <div>{r.contact?.name} — {r.contact?.role || '—'} — <a href={`mailto:${r.contact?.email}`} className="text-brand-navy font-semibold hover:underline">{r.contact?.email}</a></div>
                              <div className="text-gray-600 text-xs">{r.contact?.company} • {r.contact?.country} • {r.contact?.phone || '—'} {r.contact?.website && <> • <a href={r.contact.website} target="_blank" rel="noreferrer" className="text-brand-navy hover:underline">{r.contact.website}</a></>}</div>
                            </RfqField>
                            <RfqField label="Projet">
                              <div>{r.project?.type}{r.project?.typeOther ? ` (${r.project.typeOther})` : ''}</div>
                            </RfqField>
                            <RfqField label="Besoin">
                              <div><b>Produit :</b> {r.need?.product || '—'}</div>
                              <div className="whitespace-pre-wrap text-gray-700 mt-1">{r.need?.description || '—'}</div>
                              <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1 mt-2 text-xs text-gray-600">
                                <div><b>Quantité :</b> {r.need?.quantityValue || '—'} {r.need?.quantityUnit || ''}</div>
                                <div><b>Fréquence :</b> {r.need?.frequency || '—'}</div>
                                <div><b>Budget :</b> {r.need?.budgetAmount ? `${r.need.budgetAmount} ${r.need.budgetCurrency}` : '—'}</div>
                                <div><b>Marché :</b> {r.need?.market || '—'}</div>
                                <div><b>Échéancier :</b> {r.need?.timeline || '—'}</div>
                                <div><b>Normes :</b> {r.need?.standards || '—'}</div>
                                <div><b>Personnalisation :</b> {r.need?.customizationRequired ? `Oui — ${r.need?.customizationDetails || ''}` : 'Non'}</div>
                                <div><b>Référence :</b> {r.need?.referenceUrl ? <a href={r.need.referenceUrl} target="_blank" rel="noreferrer" className="text-brand-navy hover:underline break-all">{r.need.referenceUrl}</a> : '—'}</div>
                              </div>
                            </RfqField>
                            {r.files?.length > 0 && (
                              <RfqField label={`Pièces jointes (${r.files.length})`}>
                                <ul className="space-y-1">
                                  {r.files.map((f, i) => (
                                    <li key={i} className="flex items-center justify-between bg-white border border-gray-200 rounded px-3 py-2">
                                      <span className="text-xs truncate">{f.filename} <span className="text-gray-400">({formatSize(f.size)})</span></span>
                                      <a href={`${api.defaults.baseURL}/admin/rfq/${r.id}/files/${i}`} onClick={async (e) => { e.preventDefault(); const res = await api.get(`/admin/rfq/${r.id}/files/${i}`, { responseType: 'blob' }); const url = URL.createObjectURL(res.data); const a = document.createElement('a'); a.href = url; a.download = f.filename; a.click(); URL.revokeObjectURL(url); }} className="text-xs px-3 py-1 rounded bg-brand-yellow text-brand-navy font-semibold"><Download className="w-3.5 h-3.5 inline" /> Télécharger</a>
                                    </li>
                                  ))}
                                </ul>
                              </RfqField>
                            )}
                            <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-200">
                              <span className="text-xs text-gray-500">Statut :</span>
                              {['new', 'in_progress', 'done', 'archived'].map((s) => (
                                <button key={s} onClick={async () => { await api.patch(`/admin/rfq/${r.id}`, { status: s }); load(); }} className={`text-[11px] px-3 py-1 rounded-full border font-semibold ${r.status === s ? 'bg-brand-navy text-white border-brand-navy' : 'bg-white text-brand-navy border-gray-300 hover:border-brand-yellow'}`}>{s}</button>
                              ))}
                              <button
                                onClick={async () => {
                                  const res = await api.get(`/admin/rfq/${r.id}/pdf`, { responseType: 'blob' });
                                  const url = URL.createObjectURL(res.data);
                                  const a = document.createElement('a'); a.href = url; a.download = `rfq_${r.id.slice(0, 8)}.pdf`; a.click();
                                  URL.revokeObjectURL(url);
                                }}
                                data-testid={`rfq-pdf-${r.id}`}
                                className="inline-flex items-center gap-1 text-xs text-brand-navy hover:text-brand-yellow ml-2"
                              >
                                <Download className="w-3.5 h-3.5" /> PDF
                              </button>
                              <button
                                onClick={async () => {
                                  const res = await api.post(`/admin/rfq/${r.id}/resend-email`);
                                  if (res.data?.ok) toast({ title: 'E-mail envoyé', description: `Notification renvoyée à ${res.data.sent_to}` });
                                  else if (!res.data?.configured) toast({ title: 'Resend non configuré', description: 'Ajoute RESEND_API_KEY dans backend/.env pour activer l\'envoi.', duration: 10000 });
                                  else toast({ title: 'Envoi échoué', description: 'Vérifie les logs backend.', duration: 10000 });
                                }}
                                className="inline-flex items-center gap-1 text-xs text-brand-navy hover:text-brand-yellow"
                              >
                                <Bell className="w-3.5 h-3.5" /> Renvoyer e-mail
                              </button>
                              <button onClick={async () => { if (window.confirm('Supprimer cette demande ?')) { await api.delete(`/admin/rfq/${r.id}`); load(); } }} className="ml-auto inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700">
                                <Trash2 className="w-3.5 h-3.5" /> Supprimer
                              </button>
                            </div>

                            <MandateManager
                              rfqId={r.id}
                              rfqStatus={r.status}
                              clientEmail={r.contact?.email}
                              onAccepted={load}
                            />

                            <ShortlistManager
                              rfqId={r.id}
                              projectType={r.project?.type}
                              clientEmail={r.contact?.email}
                              isValidated={r.status === 'shortlist_validated'}
                            />

                            <ContractManager
                              rfqId={r.id}
                              clientEmail={r.contact?.email}
                              mandateAccepted={r.status === 'mandate_accepted' || r.status === 'shortlist_published' || r.status === 'shortlist_validated' || r.status === 'audit_published'}
                            />
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}

          {tab === 'mandate' && (
            <div className="space-y-6">
              <MandateTemplateEditor />
              <ContractTemplateEditor />
              <ManufacturerAgreementTemplateEditor />
              <DisclosureRegistry />
            </div>
          )}

          {tab === 'notifs' && (
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h2 className="font-bold text-brand-navy mb-4">Notifications ({unreadCount} non lues)</h2>
              {notifs.length === 0 ? <p className="text-sm text-gray-400 italic">Aucune notification.</p> : (
                <ul className="space-y-2">
                  {notifs.map((n) => (
                    <li key={n.id} className={`p-3 rounded border ${n.read ? 'border-gray-100 bg-gray-50' : 'border-brand-yellow bg-brand-yellow/5'} flex items-center justify-between`}>
                      <div>
                        <div className="text-sm text-brand-navy">{n.message}</div>
                        <div className="text-xs text-gray-500">{new Date(n.createdAt).toLocaleString()}</div>
                      </div>
                      {!n.read && <button onClick={() => markRead(n.id)} className="text-xs px-3 py-1 rounded bg-brand-navy text-white">Marquer lu</button>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}

function RfqField({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest font-bold text-brand-yellow mb-1">{label}</div>
      <div className="text-brand-navy">{children}</div>
    </div>
  );
}
